/**
 * 탭 네비게이션 컴포넌트
 */
import { Link } from 'react-router-dom';
import { Z_INDEX } from '@/constants/zIndex';
import { TAB_ROUTES } from '@/config/routes';
import { useLanguage } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useStorePricing } from '@/contexts/StorePricingContext';
import { getBottomNavigationTranslations } from './constants';
import { HomeIcon, OrdersIcon, QrCodeIcon, SaleIcon, SettingsIcon } from './Icons';
import type { NavItem } from './types';

interface TabNavProps {
  activeTab: string;
  onTabChange?: (tabId: string) => void;
}

const iconMap: Record<string, React.ComponentType<{ active: boolean }>> = {
  home: HomeIcon,
  orders: OrdersIcon,
  qr: QrCodeIcon,
  points: SaleIcon,
  admin: SettingsIcon,
};

export function TabNav({ activeTab, onTabChange }: TabNavProps) {
  const { locale } = useLanguage();
  const { isLoggedIn, hasPointsHistory } = useAuth();
  const { pointsEnabled } = useStorePricing();
  const t = getBottomNavigationTranslations(locale);

  // #285 G1 (P1 fail-open): 로그인 && (매장 포인트 활성 || prefetch 확정 없음 아님)
  // - hasPointsHistory === null (미조회 / prefetch 실패) → 노출 허용
  // - hasPointsHistory === false (확정 없음) → 숨김
  // 네트워크 일시 장애로 1회 실패해도 탭 접근 경로 유지. /points 페이지 빈 상태 UI 가 2차 방어.
  const showPointsTab = isLoggedIn && (pointsEnabled || hasPointsHistory !== false);

  const baseItems: NavItem[] = [
    { id: 'home', label: t.tabs.home, active: activeTab === 'home' },
    { id: 'orders', label: t.tabs.orders, active: activeTab === 'orders' },
    { id: 'qr', label: t.tabs.qr, active: activeTab === 'qr' },
  ];
  const pointsItem: NavItem = { id: 'points', label: t.tabs.points, active: activeTab === 'points' };
  const adminItem: NavItem = { id: 'admin', label: t.tabs.admin, active: activeTab === 'admin' };

  const navItems: NavItem[] = showPointsTab
    ? [...baseItems, pointsItem, adminItem]
    : [...baseItems, adminItem];

  return (
    // data-bottom-nav: GlobalTableSessionBar(#660)가 네비 실측 높이로 bottom offset 을
    // 계산하는 anchor 속성 (TabNav/PickupCartNav 공통).
    <nav data-bottom-nav className="fixed bottom-0 left-0 right-0 bg-muted border-t border-border" style={{ zIndex: Z_INDEX.BOTTOM_NAVIGATION }}>
      <div className="flex items-center justify-around p-[min(2.5vw,0.75rem)] max-w-md mx-auto">
        {navItems.map((item) => {
          const IconComponent = iconMap[item.id];
          return (
            <Link
              key={item.id}
              to={TAB_ROUTES[item.id] || '/'}
              onClick={onTabChange ? () => onTabChange(item.id) : undefined}
              className="flex flex-col items-center gap-[min(1vw,0.25rem)] p-[min(1.5vw,0.5rem)] rounded-lg transition-all hover:bg-background/50 active:scale-95 min-w-0"
            >
              <div className={`${item.active ? 'text-primary' : 'text-muted-foreground'}`}>
                {IconComponent && <IconComponent active={item.active || false} />}
              </div>
              <span
                className={`text-[calc(0.625rem+0.125vw)] font-medium leading-none text-center ${
                  item.active
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }`}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
